/**
 * OpsSettingsPage — admin only.
 * Manages the list of office emails that receive every incoming request.
 * Extensible: admin can add new recipients (e.g., IT, client success) at any
 * time without any code change.
 * Route: /ops/settings
 */
import { useEffect, useState } from 'react';
import { opsApi, formatAuthError } from './OpsAuthContext';
import OpsLayout from './OpsLayout';

const OpsSettingsPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [banner, setBanner] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await opsApi.get('/settings/notification-emails');
      setEmails(data.emails || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (idx, field, value) => {
    setEmails((cur) => cur.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const remove = (idx) => {
    setEmails((cur) => cur.filter((_, i) => i !== idx));
  };

  const add = () => {
    setEmails((cur) => [...cur, { email: '', label: '', active: true }]);
  };

  const save = async () => {
    setErr('');
    const cleaned = emails
      .map((e) => ({ ...e, email: (e.email || '').trim().toLowerCase() }))
      .filter((e) => e.email);
    if (cleaned.length === 0) {
      setErr('يجب أن يكون هناك بريد واحد على الأقل نشط');
      return;
    }
    setSaving(true);
    try {
      const { data } = await opsApi.put('/settings/notification-emails', { emails: cleaned });
      setEmails(data.emails);
      setBanner('تم الحفظ بنجاح ✅');
      setTimeout(() => setBanner(''), 3000);
    } catch (e) {
      setErr(formatAuthError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OpsLayout title="الإعدادات" subtitle="Notification Recipients">
      {banner && (
        <div className="mb-4 bg-[#E3EDDE] border border-[#B8CBA2] text-[#2C3814] rounded-xl px-4 py-2.5 text-[13px] font-['Noto_Kufi_Arabic']">
          {banner}
        </div>
      )}

      <section className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-6 max-w-3xl">
        <div className="mb-5">
          <h2 className="font-['Noto_Kufi_Arabic'] text-[16px] font-bold text-[#1F3D2B]">
            البُرد المُستقبِلة للطلبات الواردة من الموقع
          </h2>
          <p className="font-['Noto_Kufi_Arabic'] text-[12px] text-[#7A866E] leading-8 mt-2">
            كل طلب جديد يصل الموقع (خدمات · كفاءات · متجر · تواصل) يُرسَل نسخة
            منه إلى كل البُرد النشطة أدناه بالتوازي. يمكنك إضافة بريد جديد (مثل
            <strong> IT / خدمة العملاء</strong>) في أي وقت دون أي تعديل برمجي.
          </p>
        </div>

        {loading && (
          <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>
        )}

        {!loading && (
          <div className="space-y-3">
            {emails.map((e, i) => (
              <div
                key={i}
                data-testid={`email-row-${i}`}
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-center bg-[#FAFAF7] rounded-xl p-3"
              >
                <input
                  type="email"
                  data-testid={`email-input-${i}`}
                  value={e.email}
                  dir="ltr"
                  onChange={(ev) => update(i, 'email', ev.target.value)}
                  placeholder="name@childhorizonsconsult.com"
                  className="text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white text-start"
                />
                <input
                  type="text"
                  value={e.label}
                  onChange={(ev) => update(i, 'label', ev.target.value)}
                  placeholder="الوصف (مثال: البريد الرسمي)"
                  className="text-[13px] font-['Noto_Kufi_Arabic'] border border-[#D8D2C8] rounded-lg px-3 py-2 bg-white"
                />
                <label className="flex items-center gap-2 text-[12px] font-['Noto_Kufi_Arabic'] text-[#3D512D]">
                  <input
                    type="checkbox"
                    checked={e.active}
                    onChange={(ev) => update(i, 'active', ev.target.checked)}
                    data-testid={`email-active-${i}`}
                  />
                  نشط
                </label>
                <button
                  onClick={() => remove(i)}
                  data-testid={`email-remove-${i}`}
                  className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic'] hover:underline"
                >
                  حذف
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            data-testid="add-email-btn"
            onClick={add}
            className="text-[13px] font-['Noto_Kufi_Arabic'] border border-[#4A5D23] text-[#4A5D23] px-4 py-2 rounded-lg hover:bg-[#4A5D23] hover:text-white transition"
          >
            + إضافة بريد جديد
          </button>
          <button
            data-testid="save-emails-btn"
            onClick={save}
            disabled={saving || loading}
            className="text-[13px] font-['Noto_Kufi_Arabic'] font-semibold bg-[#1F3D2B] text-white px-6 py-2 rounded-lg hover:bg-[#163020] disabled:opacity-50"
          >
            {saving ? 'جارٍ الحفظ…' : 'حفظ التغييرات'}
          </button>
          {err && (
            <p className="text-[12px] text-[#B84A3A] font-['Noto_Kufi_Arabic']">{err}</p>
          )}
        </div>
      </section>
    </OpsLayout>
  );
};

export default OpsSettingsPage;
